import { AsyncPipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { distinctUntilChanged, lastValueFrom, map, mergeMap, startWith } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { CreateApiUserBody } from '../../../api/api-types/user.api';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { IntegrationServiceOffice } from '../../../api/app-types/integration-office.app';
import { Role } from '../../../api/app-types/role.app';
import { Specialization } from '../../../api/app-types/specialization.app';
import {
	INTEGRATION_SERVICE_SERVICE,
	IntegrationServiceService,
} from '../../../api/services/integration-service.service';
import { SearchService } from '../../../api/services/search.service';
import { USER_SERVICE, UserApi } from '../../../api/services/user.service';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { formatDateToISOString, matDateProviders } from '../../../shared/helpers/date.func';
import { hasError } from '../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { userExistsValidator } from '../../../shared/validators/user-exist-validator';
import { UserCreatedDialog } from './create-user-dialog';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { TENANT_FEATURES } from '../../../core/services/tenant-feature.service';

@Component({
	selector: 'lvr-user-create',
	templateUrl: './user-create.html',
	imports: [
		LvrPaddingDirective,
		TranslocoPipe,
		LvrInputDirective,
		ReactiveFormsModule,
		MatInputModule,
		MatFormFieldModule,
		MatSelectModule,
		MatTooltipModule,
		AddressCompletion,
		MatDatepickerModule,
		LvrAppendAsteriskPipe,
		AsyncPipe,
		LvrField,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrContactPoint,
		MatIcon,
		MatIconButton,
		LvrDefaultActions,
		LvrButton,
	],
	providers: [
		matDateProviders,
		{
			provide: INTEGRATION_SERVICE_SERVICE,
			useClass: IntegrationServiceService,
		},
		{
			provide: USER_SERVICE,
			useClass: UserApi,
		},
		SearchService,
	],
})
export class UserCreate {
	private readonly env = inject(ENVIRONMENT);
	private readonly routeData = toSignal(inject(ActivatedRoute).data);
	private readonly emailRegex = new RegExp(this.env.emailRegexStr);
	private readonly nameRegex = new RegExp(this.env.nameRegexStr);
	private readonly titleRegex = new RegExp(this.env.titleRegexStr);
	private readonly positionShareRegex = /^(0(\.\d{1,3})?|1(\.0{1,3})?)$/;
	private readonly integrationServiceApi = inject(INTEGRATION_SERVICE_SERVICE);
	private readonly userApi = inject(USER_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly router = inject(Router);
	private readonly dialog = inject(MatDialog);
	private readonly searchApi = inject(SearchService);
	private readonly session = inject(SessionManagerService);
	private readonly tenantFeatures = inject(TENANT_FEATURES);

	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly gracePeriod = this.env.gracePeriodYears;
	public readonly maxDate = signal(new Date());
	public readonly minDate = computed(() => {
		const max = this.maxDate();
		const min = new Date(max);
		min.setFullYear(max.getFullYear() - this.gracePeriod);
		return min;
	});
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly draftId = FORM_KEYS.CREATE_USER;
	public readonly hasError = hasError;
	public readonly canCreateUsersPermission = hasPermission(this.session.permissions, 'Users.Create');
	public readonly showSpecialization = computed(() => this.tenantFeatures.specializationEnabled());
	public readonly form = this.initForm();
	// Genders are coming from resolvers on route
	public readonly genders = computed(() => this.routeData()?.['genders'] ?? []);
	public readonly integrationOffices = computed<PaginatedResponse<IntegrationServiceOffice>>(
		() => this.routeData()?.['integrationOffices'] ?? [],
	);
	public readonly specializations = computed<PaginatedResponse<Specialization>>(
		() => this.routeData()?.['specializations'] ?? [],
	);
	public readonly roles = computed<PaginatedResponse<Role>>(() => this.routeData()?.['roles'] ?? []);
	private readonly integrationOfficeIdCtrl = this.form.get('employment.integrationServiceOfficeId');
	private readonly serviceIdCtrl = this.form.get('employment.serviceId');
	private readonly selectedIntegrationOfficeId = toSignal(
		this.integrationOfficeIdCtrl!.valueChanges.pipe(startWith(this.integrationOfficeIdCtrl?.value ?? undefined)),
		{ initialValue: this.integrationOfficeIdCtrl?.value ?? undefined },
	);
	public readonly isInclusionOfficeSelected = computed(() =>
		this.isInclusionOffice(this.selectedIntegrationOfficeId() ?? undefined),
	);

	public readonly loading = signal(false);

	public readonly services$ = this.integrationOfficeIdCtrl!.valueChanges.pipe(
		startWith(this.integrationOfficeIdCtrl?.value ?? undefined),
		distinctUntilChanged(),
		mergeMap((ifdId) =>
			this.integrationServiceApi.getServicesByIfd(ifdId, this.env.pagination.offset, this.env.pagination.range),
		),
		map((res) => res.entities),
	);
	private readonly syncServiceValidatorEffect = effect(() => {
		this.integrationOffices();
		this.syncServiceValidationForIfd(this.selectedIntegrationOfficeId() ?? undefined);
	});

	public readonly contactPointCategories = computed(
		() => (this.routeData()?.['contactPointCategories'].entities || []) as ContactPointCategory[],
	);
	public get contactPoints(): ContactPoint[] {
		return (this.form.get('baseInfo.contactPoints')?.value ?? []) as ContactPoint[];
	}

	public addContactPoint() {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const cp: ContactPoint = {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
		values.push(cp);
		this.form.get('baseInfo.contactPoints')?.patchValue(values);
	}

	public removeContactPoint(index: number) {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			this.form.get('baseInfo.contactPoints')?.patchValue(values);
		}
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value); // Update same item in array
		} else {
			values.push(data.value); // Add if new
		}
	}

	public cancel() {
		this.draftService.deleteDraft('user', this.draftId);
		this.router.navigate(['/admin', 'users']);
	}

	public async submit() {
		if (this.loading()) return;
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.loading.set(true);

		const formData = this.form.value;
		// Call the API to create the user
		const body: CreateApiUserBody = {
			address: {
				postalCodeAndCity: formData.baseInfo?.address?.['postalCodeAndCity'] ?? '',
				streetAndHouseNumber: formData.baseInfo?.address?.['streetAndHouseNumber'] ?? '',
			},
			businessEmail: formData.baseInfo?.businessEmail ?? '',
			firstName: formData.baseInfo?.firstName ?? '',
			name: formData.baseInfo?.name ?? '',
			notesOnAccessibility: formData.baseInfo?.notesOnAccessibility ?? '',
			remark: formData.baseInfo?.remark ?? '',
			genderId: formData.baseInfo?.genderId ?? undefined,
			positionShare: formData.employment?.positionShare ?? -1,
			roleId: formData.employment?.roleId ?? '',
			serviceId: this.resolveServiceId(
				formData.employment?.integrationServiceOfficeId ?? undefined,
				formData.employment?.serviceId ?? undefined,
			),
			specializationId: formData.employment?.specializationId ?? undefined,
			start: formatDateToISOString(formData.employment?.start) ?? '',
			title: formData.baseInfo?.title ?? '',
			contactPoints: [],
			integrationServiceOfficeId: formData.employment?.integrationServiceOfficeId ?? undefined,
		};

		const filteredCps = (formData.baseInfo?.contactPoints ?? []).filter(isContactPointComplete);

		if (filteredCps.length > 0) {
			const additionalCps = filteredCps.map((cp) => ({
				preferredWayOfContact: cp.preferredWayOfContact,
				categoryContactPointId: cp.categoryContactPointId,
				contactValue: cp.contactValue?.trim(),
				remark: cp.remark?.trim(),
			}));

			body.contactPoints?.push(...additionalCps);
		}

		try {
			const res = await this.userApi.create(body);

			// TODO: Handle this correctly after the user detail view is implemented.
			await lastValueFrom(
				this.dialog
					.open(UserCreatedDialog, {
						data: res,
					})
					.afterClosed(),
			);
			this.draftService.deleteDraft('user', this.draftId);
			this.router.navigate(['/admin', 'users']);
		} catch {
			this.notificationService.addNotification(createErrorNotification('Fehler beim Anwender anlegen'));
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * Initializes the user creation form.
	 * Wraps data in 3 groups inside the form: baseInfo, employment and office.
	 * Only for logical separation.
	 * @returns The form group for user creation.
	 */
	private initForm() {
		const fb = inject(FormBuilder);
		return fb.group({
			baseInfo: fb.group({
				title: ['', [Validators.pattern(this.titleRegex)]],
				firstName: ['', [Validators.required, Validators.pattern(this.nameRegex)]],
				name: ['', [Validators.required, Validators.pattern(this.nameRegex)]],
				notesOnAccessibility: [''],
				remark: ['', [Validators.maxLength(200)]],
				businessEmail: fb.control<string>('', {
					validators: [Validators.required, Validators.pattern(this.emailRegex)],
					asyncValidators: [userExistsValidator(this.searchApi)],
					updateOn: 'change',
				}),
				genderId: fb.control<number | undefined>(undefined, [Validators.required]),
				address: [undefined],
				contactPoints: [[] as ContactPoint[]],
			}),
			employment: fb.group({
				start: [undefined, [Validators.required]],
				positionShare: [
					undefined,
					[Validators.required, Validators.min(0), Validators.max(1), Validators.pattern(this.positionShareRegex)],
				],
				remark: [''],
				roleId: [undefined, [Validators.required]],
				serviceId: fb.control<number | undefined>(undefined, [Validators.required]),
				specializationId: [undefined],
				integrationServiceOfficeId: fb.control<number | undefined>(undefined, [Validators.required]),
			}),
		});
	}

	private resolveServiceId(integrationServiceOfficeId?: number, serviceId?: number) {
		if (this.isInclusionOffice(integrationServiceOfficeId)) return -1;
		return serviceId ?? -1;
	}

	private isInclusionOffice(integrationServiceOfficeId?: number) {
		if (integrationServiceOfficeId == null) return false;
		const office = this.integrationOffices().entities.find((x) => x.id === integrationServiceOfficeId);
		return office?.isInclusionOffice === true;
	}

	private syncServiceValidationForIfd(integrationServiceOfficeId?: number) {
		const serviceCtrl = this.serviceIdCtrl;
		if (!serviceCtrl) return;
		if (this.isInclusionOffice(integrationServiceOfficeId)) {
			serviceCtrl.clearValidators();
			serviceCtrl.setValue(-1, { emitEvent: false });
		} else {
			serviceCtrl.setValidators([Validators.required]);
			if (serviceCtrl.value === -1) {
				serviceCtrl.setValue(undefined, { emitEvent: false });
			}
		}
		serviceCtrl.updateValueAndValidity({ emitEvent: false });
	}
}
